import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { ICampaign, IDevice, IUser } from '../interfaces';
import { downloadExcelDevices, flattenCampaigns, groupCampaigns } from '../utils';
import { getShopFromDevices } from '../utils/pricing';
import {
    generateDeviceOccupation,
    generateDevicesHashMap,
    generateWeeksData,
    getHoursSumFromPlanning,
    getSignsFromDevices,
    getSlotsAvailable,
    getUsedSpace,
    IWeekData
} from '../utils/weeks-viewer';
import { ChartFreeSpace, ChartScreensUsageSigns, ChartSlotsAvailable } from './Charts';
import { getDevicesFromCamp } from './Panel';
import { PlanningBlock, PlanningWeek } from './PlanningBlock';

const getColorFromState = (state: string, canEditSigned = false) => {
    switch (state) {
        case 'passed':
            return '#fff';
        case 'signed':
            return canEditSigned ? '#fef' : '#fff';
        case 'draft':
            return '#eec';
    }

    return '#fff';
};

const CampaignCell = (props: {
    campaign: ICampaign;
    onSelectCampaign: () => void;
    users: IUser[];
    canEditSigned: boolean;
    devices: { [key: number]: IDevice };
}) => {
    const hoursSum = getHoursSumFromPlanning(props.campaign.planning);
    let user: IUser | null = null;

    if (props.campaign.planningUserId) {
        user = props.users.find(({ id }) => id === props.campaign.planningUserId) || null;
    }

    return (
        <div
            className="campaign-cell"
            style={{
                backgroundColor: getColorFromState(props.campaign.state, props.canEditSigned),
                cursor:
                    props.campaign.state === 'draft' || (props.canEditSigned && props.campaign.state === 'signed')
                        ? 'pointer'
                        : 'initial'
            }}
            onClick={() => {
                if (props.campaign.state === 'draft' || (props.canEditSigned && props.campaign.state === 'signed')) {
                    props.onSelectCampaign();
                }
            }}
        >
            <strong>
                {props.campaign.groupedIds.length !== 0 ? props.campaign.groupName : props.campaign.originalName}
                {!user || <div className="campaign-title-badge" style={{ backgroundColor: user.color }}></div>}
            </strong>
            <div className="details">
                <span>
                    {props.campaign.devices.length} écrans |{' '}
                    {Object.keys(getShopFromDevices(getDevicesFromCamp(props.campaign.devices, props.devices))).length}{' '}
                    magasins
                </span>
                <span>{hoursSum}/105 h (sem)</span>
                <span>{props.campaign.slots} slot(s)</span>
                {!user || (
                    <span>
                        Créée par {user.first_name} {user.last_name}
                    </span>
                )}
            </div>
        </div>
    );
};

const RecapCell = (props: { week: IWeekData; devices: { [key: number]: IDevice }; onClick: () => void }) => {
    const campaigns: ICampaign[] = props.week.campaigns.filter((c) => c !== null) as any;
    const devices = Object.keys(props.devices).map((d) => parseInt(d));
    const usedSpaceRatio = getUsedSpace(campaigns, devices.length, props.week.dateStart);

    return (
        <div className="recap-cell" onClick={props.onClick}>
            <p>
                Slots libres (réel):{' '}
                {getSlotsAvailable(
                    campaigns.filter(({ state }) => state !== 'draft'),
                    devices,
                    new Date(props.week.dateStart)
                )}
            </p>
            <p>Slots libres (booking): {getSlotsAvailable(campaigns, devices, new Date(props.week.dateStart))}</p>
            <div className="progress-ctr">
                <div className="num">{Math.round(10000 * usedSpaceRatio) / 100} </div>
                <div className="progress">
                    <div className="bar" style={{ width: `${100 * usedSpaceRatio}%` }}>
                        {Math.round(10000 * usedSpaceRatio) / 100}%
                    </div>
                </div>
            </div>
        </div>
    );
};

const WeekModal = (props: {
    close: () => void;
    campaigns: ICampaign[];
    devices: {
        [key: number]: IDevice;
    };
    signs: { [key: string]: number };
    weekStart: Date;
}) => {
    const [state, setState] = useState({
        open: false,
        closing: false,
        planning: new Array(7).fill(null).map(() => new Array(16).fill(null).map((_, i) => i + 7))
    });

    return (
        <div
            className={`modal-background${state.closing ? ' closing' : ''}`}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    setState({
                        ...state,
                        closing: true
                    });

                    setTimeout(props.close, 200);
                }
            }}
        >
            <div className="modal">
                <div className="availableSlots">
                    <div className="chart" style={{ width: 400 }}>
                        <ChartSlotsAvailable
                            planning={state.planning}
                            campaigns={props.campaigns}
                            devices={props.devices}
                            signs={props.signs}
                            startDate={new Date(props.weekStart)}
                        />
                    </div>
                    <div className="slots-selection">
                        <PlanningBlock
                            planning={state.planning}
                            setPlanning={(planning) => setState({ ...state, planning })}
                        />
                        <PlanningWeek
                            planning={state.planning}
                            setPlanning={(planning) => setState({ ...state, planning })}
                        />
                    </div>
                    <div className="tools">
                        <h6>Outils</h6>
                        <div className="form-group">
                            <label>Télécharger la liste des écrans (CSV)</label>
                            <br />
                            <button
                                className="btn btn-info"
                                onClick={() => {
                                    const data = generateDeviceOccupation(
                                        props.campaigns,
                                        Object.keys(props.devices).map((d) => parseInt(d)),
                                        state.planning,
                                        new Date(props.weekStart)
                                    );

                                    const devices = Object.keys(data)
                                        .filter((id) => data[parseInt(id)] < 5)
                                        .map((id) => props.devices[parseInt(id)]);

                                    downloadExcelDevices(devices);
                                }}
                            >
                                Télécharger
                            </button>
                        </div>
                    </div>
                </div>
                <ChartFreeSpace
                    campaigns={props.campaigns}
                    start={props.weekStart}
                    end={new Date(new Date(props.weekStart).setDate(props.weekStart.getDate() + 7))}
                    devices={Object.keys(props.devices).map(parseInt)}
                />
                <ChartScreensUsageSigns
                    signs={props.signs}
                    weekStart={props.weekStart}
                    campaigns={props.campaigns}
                    devices={props.devices}
                />
            </div>
        </div>
    );
};

const WeekView = (props: {
    campaigns: ICampaign[];
    devices: IDevice[];
    date: Date;
    onCampaignSelect: (camp: ICampaign) => void;
    users: IUser[];
    user: IUser;
}) => {
    const [state, setState] = useState<{
        devices: IDevice[];
        modalOpened: boolean;
        weekSelected: null | IWeekData;
    }>({
        devices: [],
        modalOpened: true,
        weekSelected: null
    });

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            devices: props.devices
        }));
    }, [props.devices]);

    const nowWeekElement = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const interval = setInterval(() => {
            if (nowWeekElement.current) {
                window.scrollTo({
                    top: nowWeekElement.current.getBoundingClientRect().top - window.innerHeight / 2 + 43,
                    behavior: 'smooth'
                });

                clearInterval(interval);
            }
        }, 200);
    }, []);

    if (state.devices.length === 0) {
        return null;
    }

    const groupedCampaigns = flattenCampaigns(groupCampaigns(props.campaigns), true);
    const { weeks, maxCampaigns } = generateWeeksData(groupedCampaigns, props.date);
    const devices = generateDevicesHashMap(state.devices);

    const now = new Date();

    return (
        <div className="weeks-view">
            <table>
                <thead>
                    <tr>
                        <th>#week</th>
                        <th>Data</th>
                        {new Array(maxCampaigns).fill(null).map((_, i) => (
                            <th key={i}>{i + 1}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((w, i) => {
                        const dateEnd = new Date(w.dateStart);
                        dateEnd.setDate(dateEnd.getDate() + 7);

                        const isNow = dateEnd.getTime() > now.getTime() && w.dateStart.getTime() <= now.getTime();

                        const campaigns: ICampaign[] = w.campaigns.filter((c) => c !== null) as any;
                        const usedSpaceRatio = getUsedSpace(campaigns, Object.keys(devices).length, w.dateStart);

                        return (
                            <tr key={i}>
                                <td>
                                    <div
                                        className={`week-no${isNow ? ' now' : ''} ${
                                            dateEnd.getDate() > 7 ? ' normal' : ' withSep'
                                        }`}
                                        ref={isNow ? nowWeekElement : null}
                                    >
                                        {i + 1}
                                        {dateEnd.getDate() > 7 || (
                                            <div className="sep">{moment(dateEnd).format('MMMM')}</div>
                                        )}
                                    </div>
                                    <div className="sep-bot" style={{ color: 'black' }}>
                                        {Math.round(10000 * usedSpaceRatio) / 100}%
                                    </div>
                                </td>
                                <td>
                                    <RecapCell
                                        week={w}
                                        devices={devices}
                                        onClick={() =>
                                            setState({
                                                ...state,
                                                modalOpened: true,
                                                weekSelected: w
                                            })
                                        }
                                    />
                                </td>
                                {w.campaigns.map((c, k) => {
                                    if (!c) {
                                        return <td key={k}></td>;
                                    }

                                    return (
                                        <td key={c.id}>
                                            <CampaignCell
                                                users={props.users}
                                                campaign={c}
                                                canEditSigned={[1, 4].includes(props.user.id)}
                                                onSelectCampaign={() => props.onCampaignSelect(c)}
                                                devices={props.devices}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {!state.modalOpened || !state.weekSelected || (
                <WeekModal
                    campaigns={state.weekSelected.campaigns.filter((c) => c !== null) as any}
                    devices={devices}
                    signs={getSignsFromDevices(Object.values(devices))}
                    weekStart={state.weekSelected.dateStart}
                    close={() => setState({ ...state, modalOpened: false, weekSelected: null })}
                />
            )}
        </div>
    );
};

export default WeekView;
